import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react'
import { TangiaLogo } from './TangiaLogo'
import { InitChroma } from './chroma'

let askedForConfig = false

interface VideoProps {
  videoName: string
  videoSrc: string
  name: string
  mirror: boolean
  similarity?: number
  smoothness?: number
  spill?: number
  // e.g. "#00FF00"
  backgroundRemovalColor?: string
  BuyerInfo?: {
    'has-plus': boolean
    name: string
  }
  settings?: {
    HideOtherVisuals: boolean
    ShowSender?: boolean
  }
}

const audioOnlyMode = window.location.search.includes('audio_only=true')
const VideoOrAudio = forwardRef<HTMLVideoElement | HTMLAudioElement, React.VideoHTMLAttributes<HTMLVideoElement> | React.AudioHTMLAttributes<HTMLAudioElement>>((props, ref) => {
  if (audioOnlyMode) {
    return <audio ref={ref as ForwardedRef<HTMLAudioElement>} {...props} />
  }
  return <video ref={ref as React.LegacyRef<HTMLVideoElement>} {...props} />
})


export default function App() {
  const [videoReady, setVideoReady] = useState(false)
  const [vol, setVol] = useState(0.7)
  const [mirrored, setMirrored] = useState(false)
  const [queryProps, setQueryProps] = useState<VideoProps | undefined>()
  const [videoCanvas, setVideoCanvas] = useState<HTMLCanvasElement | null>(null)
  const [video, setVideo] = useState<HTMLVideoElement | HTMLAudioElement | null>(null)
  const started = useRef(false)

  useEffect(() => {
    const handleMessage = async (e: MessageEvent) => {
      try {
        if (e.data.cmd === 'config') {
          setVol((old) => e.data.volume ?? old)
          setMirrored((old) => e.data.mirrored ?? old)
          console.log('setting config props')
          const configProps = e.data.config as VideoProps
          const preloadStart = new Date().getTime()
          configProps.videoSrc = await preloadMedia(configProps.videoSrc)
          reportMetric('preload_ms', new Date().getTime() - preloadStart)
          setQueryProps(configProps)
        }
        if (e.data.cmd === 'play') {
          console.log('running video interaction (maybe confetti)')
          setVideoReady(true)
          await video!.play()
        }
      } catch (error) {
        console.error('error handling message, posting to iframe')
        window.parent.postMessage({ status: 'error', error: `${error}` }, '*')
      }
    }
    window.addEventListener('message', handleMessage)
    if (!askedForConfig) {
      askedForConfig = true
      window.parent.postMessage({ status: 'readyToConfig' }, '*')
    }
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [video])

  const isReadyToPlay = !!queryProps && !!video
  useEffect(() => {
    if (!isReadyToPlay) {
      return
    }
    // this happens here, instead of after the loading code, because we want to make sure React
    // was able to do one render pass. Seems to behave differently in Safari vs Chrome
    window.parent.postMessage({ status: 'readyToPlay' }, '*')
  }, [isReadyToPlay])

  useEffect(() => {
    if (audioOnlyMode || !videoCanvas || !queryProps || !queryProps.videoSrc || !queryProps.backgroundRemovalColor) {
      return
    }
    const { similarity, smoothness, spill, backgroundRemovalColor } = queryProps
    try {
      InitChroma({
        displayCanvasEl: videoCanvas,
        webcamVideoEl: video as HTMLVideoElement,
        keyColor: backgroundRemovalColor,
        similarity,
        smoothness,
        spill
      })
    } catch (e) {
      // at this point, we could also just play the video without chroma keying, but that would look terrible on stream
      console.error('error initializing chroma', e)
      window.parent.postMessage(
        {
          status: 'error',
          error: `failed to initialize chroma - ${e}`
        },
        '*'
      )
    }
  }, [queryProps, videoCanvas, video])

  if (!queryProps) {
    return null
  }

  return (
    <div className="container">
      {queryProps.videoName === 'Confetti Surprise!' ? (
        <h1
          className="confetti"
          data-text={`${queryProps.name} sent '${queryProps.videoName || 'confetti'}' with Tangia!`}
        >{`${queryProps.name} sent '${queryProps.videoName || 'confetti'}' with Tangia!`}</h1>
      ) : !!queryProps.settings?.ShowSender && queryProps.videoName ? (
        <div
          style={{
            borderRadius: '23px',
            borderStyle: 'solid',
            borderColor: '#ADA7A7',
            height: '38px',
            display: 'flex',
            paddingLeft: '26px',
            paddingRight: '26px',
            paddingTop: '21px',
            paddingBottom: '21px',
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            backgroundColor: '#252424',
            color: '#FFF7F7',
            fontWeight: '500',
            alignItems: 'center',
            gap: '10px',
            fontSize: '32px',
            zIndex: 999
          }}
        >
          {!queryProps.BuyerInfo?.['has-plus'] && <TangiaLogo />}
          <p>
            {queryProps.BuyerInfo ? (
              <tangia-plus-name-badge {...queryProps.BuyerInfo}>{queryProps.name}</tangia-plus-name-badge>
            ) : (
              queryProps.name
            )}{' '}
            sent '{queryProps.videoName}' with Tangia!
          </p>
        </div>
      ) : null}
      <canvas
        ref={r => setVideoCanvas(r)}
        width={1920}
        height={1080}
        style={{
          display: !queryProps.backgroundRemovalColor ? 'none' : 'block',
          width: '100%',
          height: '100%',
          margin: 'auto',
          opacity: videoReady ? 1 : 0,
          transform: mirrored ? 'scaleX(-1)' : 'none'
        }}
      />
      <VideoOrAudio
        ref={r => setVideo(r)}
        crossOrigin="anonymous"
        style={{
          display: queryProps.backgroundRemovalColor ? 'none' : 'block',
          width: '100%',
          height: '100%',
          margin: 'auto',
          opacity: videoReady ? 1 : 0,
          transform: mirrored && !queryProps.backgroundRemovalColor ? 'scaleX(-1)' : 'none'
        }}
        src={queryProps.videoSrc}
        autoPlay={false}
        onPlaying={(e: Parameters<NonNullable<React.VideoHTMLAttributes<HTMLVideoElement>['onPlaying']>>[0] | Parameters<NonNullable<React.AudioHTMLAttributes<HTMLAudioElement>['onPlaying']>>[0]) => {
          e.currentTarget.volume = vol
          if (started.current) {
            console.log('restarted playing')
            return
          }
          started.current = true
          console.log('playing')
          window.parent.postMessage({ status: 'playing' }, '*')
          if (e.currentTarget.duration && Number.isFinite(e.currentTarget.duration)) {
            const fullDurationWithBuffer = e.currentTarget.duration + 10
            window.parent.postMessage({ status: 'duration_update', duration: fullDurationWithBuffer }, '*')
          }
        }}
        onError={(e: Parameters<NonNullable<React.VideoHTMLAttributes<HTMLVideoElement>['onError']>>[0] | Parameters<NonNullable<React.AudioHTMLAttributes<HTMLAudioElement>['onError']>>[0]) => {
          window.parent.postMessage(
            {
              status: 'error',
              error: `${e.currentTarget.error} "${e.currentTarget.error?.message}" - code: ${e.currentTarget.error?.code}`
            },
            '*'
          )
        }}
        onEnded={() => {
          console.log('ending')
          setVideoReady(false)
          window.parent.postMessage({ status: 'ended' }, '*')
        }}
      />
    </div>
  )
}

async function preloadMedia(url: string, opts?: { timeoutMs: number; retries: number }) {
  const timeoutMs = opts?.timeoutMs ?? 60_000
  const retries = opts?.retries ?? 3
  const timeout = new Promise<never>((_, reject) => setTimeout(reject, timeoutMs))
  for (let i = 0; i < retries; i++) {
    try {
      const response = await Promise.race([fetch(url), timeout])
      if (!response.ok) {
        console.log(`failed to preload media - response not ok: ${response.status}`)
        continue
      }
      const blob = await Promise.race([response.blob(), timeout])
      return URL.createObjectURL(blob)
    } catch (e) {
      console.log('error preloading media - retrying', e)
    }
  }
  console.error('failed to preload audio - continuing with original')
  return url
}

interface MetricEvent {
  status: 'metric';
  metric: string;
  value: number;
}

export function reportMetric(metric: string, value: number) {
  window.parent?.postMessage({ status: 'metric', metric, value } as MetricEvent, '*')
}